import { CustomQuestionnaireSummary } from '@api';
import { QuickStart, Tooltip } from '@components/ui';
import { depaginate, useApi } from '@data-access';
import { AppRoutes } from '@types';
import clsx from 'clsx';
import { ChevronDown, ClipboardCheck, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getSidebarBottomLinks, getSidebarTopLinks, SidebarEntry } from './Sidebar.mocks';
import styles from './Sidebar.module.scss';

const isItemActive = (item: SidebarEntry, pathname: string): boolean => {
  if (!item.href) return false;
  if (pathname === item.href) return true;
  if (pathname?.startsWith(`${item.href}/`)) return true;
  if (item.activeForPrefixes?.some(p => pathname?.startsWith(p))) return true;
  return false;
};

const isAnySubitemActive = (subitems: SidebarEntry[], pathname: string): boolean =>
  subitems.some(
    sub => isItemActive(sub, pathname) || (sub.subitems ? isAnySubitemActive(sub.subitems, pathname) : false),
  );

interface NavItemProps {
  item: SidebarEntry;
  level?: number;
  isExpanded: boolean;
}

const NavItem = ({ item, level = 0, isExpanded }: NavItemProps) => {
  const { pathname } = useRouter();
  const active = isItemActive(item, pathname);
  const Icon = item.lucideIcon;
  const iconEl = Icon ? <Icon className={styles.itemIcon} /> : null;
  const itemCls = clsx(level > 0 ? styles.subItem : styles.navItem, { [styles.active]: active });
  const activeAttr = active ? { 'data-nav-active': '' } : {};

  if (!isExpanded) {
    let trigger: React.ReactElement | null = null;
    if (item.onClick) {
      trigger = (
        <button
          {...activeAttr}
          className={itemCls}
          onClick={item.onClick}
        >
          {iconEl}
        </button>
      );
    } else if (item.href && item.external) {
      trigger = (
        <a
          {...activeAttr}
          className={itemCls}
          href={item.href}
          data-testid={item.testId}
        >
          {iconEl}
        </a>
      );
    } else if (item.href) {
      trigger = (
        <Link
          {...activeAttr}
          className={itemCls}
          href={item.href}
          data-testid={item.testId}
        >
          {iconEl}
        </Link>
      );
    }

    if (!trigger) return null;
    return (
      <Tooltip
        content={item.label}
        placement="right"
        hideArrow
        isFixed
        className={styles.navTooltip}
      >
        {trigger}
      </Tooltip>
    );
  }

  const label = <span className={styles.itemLabel}>{item.label}</span>;

  if (item.onClick) {
    return (
      <button
        {...activeAttr}
        className={itemCls}
        onClick={item.onClick}
      >
        {iconEl}
        {label}
      </button>
    );
  }
  if (item.href && item.external) {
    return (
      <a
        {...activeAttr}
        className={itemCls}
        href={item.href}
        data-testid={item.testId}
      >
        {iconEl}
        {label}
      </a>
    );
  }
  if (item.href) {
    return (
      <Link
        {...activeAttr}
        className={itemCls}
        href={item.href}
        data-testid={item.testId}
      >
        {iconEl}
        {label}
      </Link>
    );
  }
  return null;
};

interface NavSubGroupProps {
  group: SidebarEntry;
  isExpanded: boolean;
}

const NavSubGroup = ({ group, isExpanded }: NavSubGroupProps) => {
  const { pathname } = useRouter();
  const [isOpen, setIsOpen] = useState(
    () => (group.defaultOpen ?? true) || isAnySubitemActive(group.subitems ?? [], pathname),
  );

  useEffect(() => {
    if (group.subitems && isAnySubitemActive(group.subitems, pathname) && !isOpen) {
      setIsOpen(true);
    }
  }, [group.subitems, isOpen, pathname]);

  if (!isExpanded) return null;

  return (
    <div className={styles.subGroup}>
      <button
        className={styles.subGroupHeader}
        onClick={() => setIsOpen(prev => !prev)}
      >
        <span className={styles.subGroupLabel}>{group.label}</span>
        <ChevronDown className={clsx(styles.chevron, { [styles.chevronOpen]: isOpen })} />
      </button>
      {isOpen && (
        <div className={styles.groupItems}>
          {group.subitems?.map(sub => (
            <NavItem
              key={sub.href || sub.label}
              item={sub}
              level={1}
              isExpanded={isExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface NavGroupProps {
  group: SidebarEntry;
  isExpanded: boolean;
  isOpen: boolean;
  onToggle: (label: string) => void;
  onExpand: () => void;
}

const NavGroup = ({ group, isExpanded, isOpen, onToggle, onExpand }: NavGroupProps) => {
  const { pathname } = useRouter();
  const GroupIcon = group.lucideIcon;
  const groupActive =
    (group.subitems ? isAnySubitemActive(group.subitems, pathname) : false) ||
    (group.activeForPrefixes?.some(p => pathname?.startsWith(p)) ?? false);

  if (!isExpanded) {
    return (
      <Tooltip
        content={group.label}
        placement="right"
        hideArrow
        isFixed
        className={styles.navTooltip}
      >
        <button
          className={clsx(styles.groupHeader, { [styles.active]: groupActive })}
          onClick={onExpand}
        >
          {GroupIcon && <GroupIcon className={styles.itemIcon} />}
        </button>
      </Tooltip>
    );
  }

  return (
    <div className={styles.group}>
      <button
        className={styles.groupHeader}
        onClick={() => onToggle(group.label)}
      >
        {GroupIcon && <GroupIcon className={styles.itemIcon} />}
        <span className={styles.itemLabel}>{group.label}</span>
        <ChevronDown className={clsx(styles.chevron, { [styles.chevronOpen]: isOpen })} />
      </button>
      {isOpen && (
        <div className={styles.groupItems}>
          {group.subitems?.map(sub => {
            if (sub.isSubGroup) {
              return (
                <NavSubGroup
                  key={sub.label}
                  group={sub}
                  isExpanded={isExpanded}
                />
              );
            }
            if (sub.subtitle) {
              return (
                <div
                  key={sub.label}
                  className={styles.sectionLabel}
                >
                  {sub.label}
                </div>
              );
            }
            return (
              <NavItem
                key={sub.href || sub.label}
                item={sub}
                level={1}
                isExpanded={isExpanded}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export const Sidebar = observer(() => {
  const { customQuestionnaireApi } = useApi();
  const { pathname } = useRouter();

  const [showModal, setShowModal] = useState(false);
  const [initialStep, setInitialStep] = useState(0);
  const [customQuestionnaires, setCustomQuestionnaires] = useState<CustomQuestionnaireSummary[]>(() => []);
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sidebar-expanded') !== 'false';
    }
    return true;
  });
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(['Data Management', 'Assessments', 'Results']),
  );

  const navRef = useRef<HTMLElement>(null);

  const sidebarBottomLinks = getSidebarBottomLinks();
  sidebarBottomLinks[0].onClick = setShowModal.bind(null, true);

  const fetchCustomQuestionnaires = useCallback(async () => {
    const response = await depaginate(page => customQuestionnaireApi.apiCustomQuestionnaireList({ page }));
    setCustomQuestionnaires(response);
  }, [customQuestionnaireApi]);

  useEffect(() => {
    fetchCustomQuestionnaires();
  }, [fetchCustomQuestionnaires]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { hash } = window.location;
      if (hash === '#welcome' || hash.startsWith('#quick-start')) {
        const stepParam = hash.split('=')[1];
        const stepNum = stepParam ? parseInt(stepParam, 10) : 1;
        setInitialStep(Number.isFinite(stepNum) && stepNum >= 1 ? stepNum - 1 : 0);
        setShowModal(true);
      }
    }
  }, []);

  const onClose = () => {
    if (typeof window !== 'undefined') {
      if (window.location.hash === '#welcome') {
        setShowModal(false);
        window.location.replace(`/cal/${AppRoutes.dashboard}`);
      } else {
        setShowModal(false);
      }
    }
  };

  const toggleGroup = (label: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const expandSidebar = () => {
    setIsExpanded(true);
    if (typeof window !== 'undefined') {
      localStorage.setItem('sidebar-expanded', 'true');
    }
  };

  const toggleSidebar = () => {
    setIsExpanded(prev => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem('sidebar-expanded', String(next));
      }
      return next;
    });
  };

  // Insert dynamic questionnaires into the Governance Evaluation sub-group under Assessments
  const topLinks = getSidebarTopLinks().map(link => {
    if (link.label === 'Assessments' && link.subitems) {
      return {
        ...link,
        subitems: link.subitems.map(sub => {
          if (sub.isSubGroup && sub.id === 'governance-evaluation') {
            const staticSubitems = (sub.subitems ?? []).filter(
              item =>
                !(
                  typeof item.href === 'string' &&
                  item.href.startsWith(`${AppRoutes.governanceEvaluation}?questionnaire=`)
                ),
            );
            return {
              ...sub,
              subitems: [
                ...staticSubitems,
                ...customQuestionnaires.map(q => ({
                  href: `${AppRoutes.governanceEvaluation}?questionnaire=${q.id}`,
                  label: q.name,
                  lucideIcon: ClipboardCheck,
                })),
              ],
            };
          }
          return sub;
        }),
      };
    }
    return link;
  });

  const filteredTopLinks = topLinks;

  // Auto-expand sub-groups that contain the active route
  const filteredTopLinksRef = useRef(filteredTopLinks);
  filteredTopLinksRef.current = filteredTopLinks;

  useEffect(() => {
    filteredTopLinksRef.current.forEach(link => {
      link.subitems?.forEach(sub => {
        if (sub.isSubGroup && sub.subitems && isAnySubitemActive(sub.subitems, pathname)) {
          setOpenGroups(prev => {
            if (prev.has(sub.label)) return prev;
            return new Set([...prev, sub.label]);
          });
        }
      });
    });
  }, [pathname]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const el = navRef.current?.querySelector<HTMLElement>('[data-nav-active]');
      if (!el || !navRef.current) return;
      const PADDING = 40;
      const nav = navRef.current;
      const navRect = nav.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      if (elRect.bottom + PADDING > navRect.bottom) {
        nav.scrollBy({ top: elRect.bottom + PADDING - navRect.bottom, behavior: 'smooth' });
      } else if (elRect.top - PADDING < navRect.top) {
        nav.scrollBy({ top: elRect.top - PADDING - navRect.top, behavior: 'smooth' });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [pathname, customQuestionnaires]);

  return (
    <>
      <aside
        id="sidebar"
        className={clsx(styles.sidebar, isExpanded ? styles.expanded : styles.collapsed)}
      >
        <nav
          ref={navRef as React.Ref<HTMLElement>}
          className={styles.nav}
        >
          {filteredTopLinks.map((link, i) => {
            if (link.isGroup) {
              return (
                <NavGroup
                  key={link.label}
                  group={link}
                  isExpanded={isExpanded}
                  isOpen={openGroups.has(link.label)}
                  onToggle={toggleGroup}
                  onExpand={expandSidebar}
                />
              );
            }
            return (
              <NavItem
                key={link.href || `link-${i}`}
                item={link}
                isExpanded={isExpanded}
              />
            );
          })}
        </nav>

        <div className={styles.bottomSection}>
          {sidebarBottomLinks.map((link, i) => (
            <NavItem
              key={link.href || `bottom-${i}`}
              item={link}
              isExpanded={isExpanded}
            />
          ))}
        </div>

        <div className={styles.toggleWrapper}>
          <button
            className={styles.toggleBtn}
            onClick={toggleSidebar}
            aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {isExpanded ? (
              <PanelLeftClose className={styles.toggleIcon} />
            ) : (
              <PanelLeftOpen className={styles.toggleIcon} />
            )}
          </button>
        </div>
      </aside>
      <QuickStart
        showModal={showModal}
        onClose={onClose}
        initialStep={initialStep}
      />
    </>
  );
});
